import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import { cn, getErrorMessage } from "@/lib/utils";
import { CalendarIcon, Plus, Pencil, Trash2, ImageIcon } from "lucide-react";

interface EventItem {
  id: string;
  title: string;
  description: string | null;
  event_date: string;
  image_url: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

const Events = () => {
  const { t } = useLanguage();
  const { isAdmin, user } = useAuth();
  const [events, setEvents] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<EventItem | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [eventDate, setEventDate] = useState<Date>();
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const fetchEvents = async () => {
    const { data, error } = await supabase
      .from("events")
      .select("*")
      .order("event_date", { ascending: false });

    if (!error && data) {
      setEvents(data as EventItem[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setEventDate(undefined);
    setImageFile(null);
    setImagePreview(null);
    setSelectedEvent(null);
  };

  const openAddDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (event: EventItem) => {
    setSelectedEvent(event);
    setTitle(event.title);
    setDescription(event.description || "");
    setEventDate(new Date(event.event_date));
    setImagePreview(event.image_url);
    setImageFile(null);
    setDialogOpen(true);
  };

  const openDeleteDialog = (event: EventItem) => {
    setSelectedEvent(event);
    setDeleteDialogOpen(true);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async (file: File): Promise<string | null> => {
    const ext = file.name.split(".").pop();
    const fileName = `${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage
      .from("event-images")
      .upload(fileName, file);

    if (error) {
      console.error("Upload error:", error);
      return null;
    }

    const { data } = supabase.storage
      .from("event-images")
      .getPublicUrl(fileName);
    return data.publicUrl;
  };

  const deleteStorageFile = async (url: string) => {
    try {
      const parts = url.split("/event-images/");
      if (parts[1]) {
        await supabase.storage.from("event-images").remove([parts[1]]);
      }
    } catch (e) {
      console.error("Error deleting file:", e);
    }
  };

  const handleSave = async () => {
    if (!title.trim() || !eventDate) {
      toast.error("Judul dan tanggal acara wajib diisi");
      return;
    }

    setSaving(true);
    try {
      let imageUrl = selectedEvent?.image_url || null;

      if (imageFile) {
        // Delete old image if editing
        if (selectedEvent?.image_url) {
          await deleteStorageFile(selectedEvent.image_url);
        }
        imageUrl = await uploadImage(imageFile);
        if (!imageUrl) {
          toast.error("Gagal mengupload gambar");
          setSaving(false);
          return;
        }
      }

      const eventData = {
        title: title.trim(),
        description: description.trim() || null,
        event_date: format(eventDate, "yyyy-MM-dd"),
        image_url: imageUrl,
      };

      if (selectedEvent) {
        const { error } = await supabase
          .from("events")
          .update(eventData)
          .eq("id", selectedEvent.id);
        if (error) throw error;
        toast.success("Event berhasil diperbarui");
      } else {
        const { error } = await supabase
          .from("events")
          .insert({ ...eventData, created_by: user?.id });
        if (error) throw error;
        toast.success("Event berhasil ditambahkan");
      }

      setDialogOpen(false);
      resetForm();
      fetchEvents();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedEvent) return;
    try {
      if (selectedEvent.image_url) {
        await deleteStorageFile(selectedEvent.image_url);
      }
      const { error } = await supabase
        .from("events")
        .delete()
        .eq("id", selectedEvent.id);
      if (error) throw error;
      toast.success("Event berhasil dihapus");
      setDeleteDialogOpen(false);
      setSelectedEvent(null);
      fetchEvents();
    } catch (error) {
      toast.error(getErrorMessage(error, "Gagal menghapus event"));
    }
  };

  return (
    <section id="events" className="py-12 sm:py-16 md:py-20 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="text-center mb-8 sm:mb-10 md:mb-12 animate-fade-up">
          <h2 className="text-3xl sm:text-4xl font-bold mb-3 sm:mb-4">{t.events.title}</h2>
          <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto">
            {t.events.subtitle}
          </p>
        </div>

        {isAdmin && (
          <div className="flex justify-center mb-6">
            <Button onClick={openAddDialog} className="gap-2">
              <Plus className="h-4 w-4" />
              Tambah Event
            </Button>
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <div className="h-48 bg-muted rounded-t-lg" />
                <CardContent className="p-4 space-y-2">
                  <div className="h-4 bg-muted rounded w-3/4" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                  <div className="h-3 bg-muted rounded w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : events.length === 0 ? (
          <div className="max-w-4xl mx-auto">
            <Card className="shadow-soft animate-fade-up">
              <CardContent className="p-6 sm:p-8 md:p-12 text-center">
                <div className="max-w-md mx-auto">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 mb-4">
                    <ImageIcon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{t.events.noImages}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t.events.maintenanceDesc}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {events.map((event) => (
              <Card
                key={event.id}
                className="overflow-hidden shadow-soft hover-glow transition-smooth group"
              >
                {event.image_url ? (
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={event.image_url}
                      alt={event.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="h-48 bg-muted flex items-center justify-center">
                    <ImageIcon className="w-12 h-12 text-muted-foreground/40" />
                  </div>
                )}
                <CardContent className="p-4 sm:p-5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-lg leading-tight mb-1 truncate">
                        {event.title}
                      </h3>
                      <p className="text-xs text-muted-foreground mb-2">
                        {format(new Date(event.event_date), "dd MMMM yyyy")}
                      </p>
                    </div>
                    {isAdmin && (
                      <div className="flex gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openEditDialog(event)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => openDeleteDialog(event)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                  {event.description && (
                    <p className="text-sm text-muted-foreground line-clamp-3">
                      {event.description}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedEvent ? "Edit Event" : "Tambah Event Baru"}
            </DialogTitle>
            <DialogDescription>
              {selectedEvent
                ? "Perbarui informasi event"
                : "Isi detail event yang ingin ditambahkan"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label htmlFor="event-title">Judul Acara *</Label>
              <Input
                id="event-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Masukkan judul acara"
              />
            </div>

            <div className="space-y-2">
              <Label>Tanggal Acara *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !eventDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {eventDate ? format(eventDate, "dd MMMM yyyy") : "Pilih tanggal"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={eventDate}
                    onSelect={setEventDate}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label htmlFor="event-image">Foto Acara</Label>
              <Input
                id="event-image"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />
              {imagePreview && (
                <div className="mt-2 rounded-lg overflow-hidden border">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full h-40 object-cover"
                  />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="event-desc">Deskripsi Kegiatan</Label>
              <Textarea
                id="event-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Masukkan deskripsi acara"
                rows={4}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setDialogOpen(false)}
                disabled={saving}
              >
                Batal
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? "Menyimpan..." : "Simpan"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Event</AlertDialogTitle>
            <AlertDialogDescription>
              Apakah Anda yakin ingin menghapus event "{selectedEvent?.title}"?
              Tindakan ini tidak dapat dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};

export default Events;
